import { Plus, X } from "lucide-react";
import {
  useLocaleState,
  useNotify,
  useTranslate,
  useUpdate,
  type Identifier,
} from "ra-core";
import { Link } from "react-router";
import { ReferenceField } from "@/components/admin/reference-field";
import { Button } from "@/components/ui/button";

import { useDealStatusChoices } from "../deals/dealStatuses";
import { useCalendarSync } from "../events/useCalendarSync";
import { ChoiceBadge } from "../misc/ChoiceBadge";
import { RecordPicker } from "../misc/RecordPicker";
import { formatLocalizedDate } from "../misc/RelativeDate";
import type { Deal } from "../types";
import { usePartnerDeals } from "./usePartnerDeals";

/** « Opportunités » tab of a partner: the ones it works on, to add or leave. */
export const PartnerDeals = ({ partnerId }: { partnerId: Identifier }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [locale = "fr"] = useLocaleState();
  const statusChoices = useDealStatusChoices();
  const { data: deals = [], isPending, refetch } = usePartnerDeals(partnerId);
  const [update, { isPending: isSaving }] = useUpdate<Deal>();
  const syncCalendar = useCalendarSync();

  const setStaffed = (deal: Deal, isStaffed: boolean) => {
    const partnerIds = deal.partner_ids ?? [];
    update(
      "deals",
      {
        id: deal.id,
        data: {
          partner_ids: isStaffed
            ? [...partnerIds, partnerId]
            : partnerIds.filter((id) => id !== partnerId),
        },
        previousData: deal,
      },
      {
        onSuccess: () => {
          refetch();
          // The opportunity's events follow its staff.
          syncCalendar({ deal_id: deal.id });
        },
        onError: () =>
          notify("resources.partners.link_error", { type: "error" }),
      },
    );
  };

  return (
    <div className="flex flex-col gap-3 pt-2">
      <div className="flex justify-end">
        <RecordPicker<Deal>
          resource="deals"
          label={translate("resources.partners.add_to_deal")}
          icon={<Plus className="size-4" />}
          searchPlaceholder={translate("resources.partners.search_deal")}
          emptyText={translate("crm.filters.no_match")}
          excludedIds={deals.map((deal) => deal.id)}
          filter={{ "archived_at@is": null }}
          sort={{ field: "expected_closing_date", order: "DESC" }}
          disabled={isSaving}
          renderOption={(deal) => (
            <span className="flex min-w-0 flex-col">
              <span className="truncate">{deal.name}</span>
              <span className="truncate text-xs text-muted-foreground">
                <ReferenceField
                  record={deal}
                  source="company_id"
                  reference="companies"
                  link={false}
                />
              </span>
            </span>
          )}
          onSelect={(deal) => setStaffed(deal, true)}
        />
      </div>
      {isPending ? null : deals.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {translate("resources.partners.no_deals")}
        </p>
      ) : (
        <ul className="divide-y">
          {deals.map((deal) => (
            <li key={deal.id} className="flex items-center gap-3 py-2 text-sm">
              <Link
                to={`/deals/${deal.id}/show`}
                className="flex-1 min-w-0 rounded-sm hover:bg-muted"
              >
                <span className="block font-medium truncate">{deal.name}</span>
                <span className="block text-muted-foreground truncate">
                  <ReferenceField
                    record={deal}
                    source="company_id"
                    reference="companies"
                    link={false}
                  />
                </span>
              </Link>
              {deal.expected_closing_date ? (
                <span className="hidden sm:inline text-muted-foreground shrink-0">
                  {formatLocalizedDate(deal.expected_closing_date, locale)}
                </span>
              ) : null}
              <ChoiceBadge choices={statusChoices} value={deal.status} />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                disabled={isSaving}
                aria-label={translate("resources.partners.remove_from", {
                  name: deal.name,
                })}
                onClick={() => setStaffed(deal, false)}
              >
                <X className="size-4" />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
